import React from 'react';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';

import { pluralizeCount } from '../../helpers/formatting';
import { getLinkChoices } from '../../helpers/links_to_check_flow_helpers';
import { SKIP_REASONS, getAlertSubhead, getListingState, getUsualFix } from '../../helpers/links_to_check_helpers';
import { isMergeAnswer } from '../../helpers/links_to_check_likely_wrong_helpers';
import { formatPrice, getProductAdminUrl } from '../../helpers/url_data_agent_live_matches_helpers';
import SafeImage from '../PinMatchSilentEval/SafeImage';
import { useLinksToCheckContext } from './LinksToCheckContext';
import LinkCreate from './LinkCreate';
import LinkSearch from './LinkSearch';

import './LinkCardCatalog.scss';

/** Our side of the card: our pick, what we'd add, what a skip records, what we did on our own, or the search or New Product form opened on it. */
const LinkCardCatalog = () => {
  const { link, pane, mergeBlockedReason, answer } = useLinksToCheckContext();
  const view = getCatalogView(link, pane);
  // A clicked choice gives focus back, so Enter or Space can't answer the next card by accident.
  const pickChoice = (product, event) => {
    event.currentTarget.blur();
    answer({ kind: 'link', product });
  };
  const choices = getLinkChoices(link).map((product, index) => ({ product, keyLabel: String(index + 1), onPick: event => pickChoice(product, event) }));
  return <div className={cn('links-to-check-card-catalog', view)}>{CATALOG_VIEWS[view](link, choices, { canMerge: !mergeBlockedReason })}</div>;
};

export default LinkCardCatalog;

const getCatalogView = (link, pane) => {
  if (pane) return pane;
  if (link.cardType === 'same_item') return 'pair';
  if (link.cardType === 'alert') return 'applied';
  if (link.question === 'new_product') return 'proposal';
  if (link.question === 'skip') return 'skip';
  if (link.question === 'what_is_it') return 'search';
  return link.pick ? 'pick' : 'choices';
};

const renderPick = ({ pick }, choices) => {
  const pickKey = choices.find(choice => choice.product.id === pick.id)?.keyLabel;
  const otherChoices = choices.filter(choice => choice.product.id !== pick.id);
  return (
    <>
      <div className='side-label'>
        <span>In our catalog</span>
        {choices.length > 0 && <span>{choices.length > 1 ? `${choices[0].keyLabel}–${_.last(choices).keyLabel} to link` : `${choices[0].keyLabel} to link`}</span>}
      </div>
      <div className='catalog-pick'>
        <span className='catalog-badges'>
          <span className='catalog-badge'>Our pick</span>
          {pickKey && <span className='ltc-key'>{pickKey}</span>}
        </span>
        {renderProductBlock(pick)}
      </div>
      {otherChoices.length > 0 && (
        <>
          <div className='catalog-subhead'>Other close matches</div>
          <div className='catalog-rows'>{otherChoices.map(choice => renderProductRow(choice.product, choice))}</div>
        </>
      )}
    </>
  );
};

// A pick that was deleted or merged arrives as no pick on a same-product answer.
const renderChoices = ({ verdict }, choices) => (
  <>
    <div className='side-label'>
      <span>In our catalog</span>
      {choices.length > 0 && <span>press {choices.map(choice => choice.keyLabel).join(', ')} to link</span>}
    </div>
    {verdict === 'sibling' && <div className='catalog-gone'>Our pick is no longer in the catalog.</div>}
    {choices.length ? (
      <div className='catalog-tiles'>{choices.map(renderProductTile)}</div>
    ) : (
      <div className='catalog-subhead'>We found nothing close in the catalog.</div>
    )}
  </>
);

const renderProposal = ({ proposal, page }, choices) => {
  const brandSuggestion = proposal?.brandSuggestions?.[0];
  const brandStatus = proposal?.brandInCatalog ? (
    <span className='catalog-ok'>In the catalog</span>
  ) : (
    <>
      <span className='catalog-missing'>Not in the catalog</span>
      {brandSuggestion && (
        <>
          {' · close spelling: '}
          <b>{brandSuggestion.name}</b> ({pluralizeCount(brandSuggestion.productCount, 'product')})
        </>
      )}
    </>
  );
  const fields = [
    { label: 'Name', value: <b>{proposal?.title || page?.title}</b> },
    {
      label: 'Brand',
      value: (
        <span>
          {proposal?.brand || page?.brand} · {brandStatus}
        </span>
      )
    },
    { label: 'Category', value: proposal?.category },
    { label: 'Price', value: formatPrice(proposal?.price, proposal?.currency) },
    { label: 'Condition', value: page?.isPreownedShop && 'Pre-owned: one listing, one product' }
  ].filter(field => field.value);
  return (
    <>
      <div className='side-label'>
        <span>What we’d add</span>
      </div>
      <div className='catalog-product'>
        <div className='catalog-photo'>
          <SafeImage className='ltc-photo-image' source={proposal?.image || page?.image} alt='' placeholderText='No photo from the page' />
        </div>
        {renderFields(fields)}
      </div>
      <div className='catalog-subhead'>{choices.length ? 'Already in the catalog? Link it instead' : 'Nothing with a close name in the catalog.'}</div>
      {choices.length > 0 && <div className='catalog-rows'>{choices.map(choice => renderProductRow(choice.product, choice))}</div>}
    </>
  );
};

// An out-of-stock listing can come back, so no reason is chosen for it up front.
const renderSkip = link => {
  const listing = getListingState(link);
  const dismissReason = listing === 'sold' ? 'sold_one_of_a_kind' : 'not_a_product_page';
  const fields = _.compact([
    listing !== 'out_of_stock' && { label: 'Reason', value: <b>{_.find(SKIP_REASONS, { dismissReason }).display}</b> },
    { label: 'The link', value: `Stays unmatched.${link.creators?.total ? ` ${getCreatorPinsLine(link.creators.total)}` : ''}` },
    { label: 'Instead', value: listing ? 'If it’s still for sale, add it as its own pre-owned product.' : 'If the page shows the one product the creator meant, add it instead.' }
  ]);
  return (
    <>
      <div className='side-label'>
        <span>If you skip it</span>
      </div>
      {renderFields(fields)}
    </>
  );
};

const renderApplied = (link, choices, { canMerge }) => {
  const { applied } = link;
  const usualFix = getUsualFix(link, { canMerge });
  const subhead = getAlertSubhead(link, { hasCloseMatches: choices.length > 0, canMerge });
  const canPick = canMerge || !isMergeAnswer(link, { kind: 'alert_repoint' });
  return (
    <>
      <div className='side-label'>
        <span>{applied.action === 'create' ? 'We added' : 'We linked it to'}</span>
        {usualFix && <span>{usualFix}</span>}
      </div>
      <div className='catalog-pick alert'>
        <span className='catalog-badges'>
          <span className='catalog-badge'>What we did</span>
        </span>
        {renderProductBlock(applied.product)}
      </div>
      <div className='catalog-subhead'>{subhead}</div>
      {choices.length > 0 && <div className='catalog-rows'>{choices.map(choice => renderProductRow(choice.product, canPick ? choice : {}))}</div>}
    </>
  );
};

const renderPair = ({ pair, alert }) => {
  const sides = [
    { key: 'remove', title: alert ? 'We added' : 'Newer product', detail: 'removed if you merge', product: pair.remove, note: getMovesLabel(pair.moves) },
    { key: 'keep', title: 'Already in the catalog', detail: 'kept if you merge', product: pair.keep }
  ];
  return sides.map(side => (
    <div key={side.key} className='catalog-pair-side'>
      <div className='side-label'>
        <span>{side.title}</span>
        <span>{side.detail}</span>
      </div>
      {renderProductBlock(side.product, { note: side.note })}
    </div>
  ));
};

const CATALOG_VIEWS = {
  pick: renderPick,
  choices: renderChoices,
  search: link => <LinkSearch key={link.id} />,
  create: () => <LinkCreate />,
  proposal: renderProposal,
  skip: renderSkip,
  applied: renderApplied,
  pair: renderPair
};

const getCreatorPinsLine = total => `Its ${pluralizeCount(total, 'creator pin')} ${total === 1 ? 'keeps' : 'keep'} pointing at the page.`;

const getMovesLabel = moves => moves && `Merging moves ${pluralizeCount(moves.links, 'link')} and ${pluralizeCount(moves.pins, 'creator pin')}`;

const renderFields = fields => (
  <div className='catalog-fields'>
    {fields.map(field => (
      <div key={field.label} className='catalog-field'>
        <span>{field.label}</span>
        {field.value}
      </div>
    ))}
  </div>
);

const renderProductBlock = (product, { note } = {}) => {
  const facts = [formatPrice(product.fallbackPrice, product.fallbackPriceCurrency), product.category?.name].filter(Boolean).join(' · ');
  const source = [product.fallbackHost && `listed from ${product.fallbackHost}`, product.isUsed && 'Pre-owned', product.isHidden && 'Hidden from shoppers'].filter(Boolean);
  return (
    <div className='catalog-product'>
      <div className='catalog-photo'>
        <SafeImage className='ltc-photo-image' source={product.image} alt='' placeholderText='No photo' />
      </div>
      <div className='catalog-product-info'>
        {product.brand && <div className='catalog-brand'>{product.brand}</div>}
        <div className='catalog-title'>{product.title}</div>
        {facts && <div className='catalog-facts'>{facts}</div>}
        <div className='catalog-muted'>
          <a href={getProductAdminUrl(product.id)} target='_blank' rel='noopener noreferrer'>
            #{product.id}
          </a>
          {source.map(text => ` · ${text}`)}
        </div>
        {product.createdAt && <div className='catalog-muted'>Added {moment(product.createdAt).format('MMM D, YYYY')}</div>}
        {note && <div className='catalog-muted'>{note}</div>}
      </div>
    </div>
  );
};

const renderProductRow = (product, { keyLabel, onPick }) => {
  const meta = [product.brand, formatPrice(product.fallbackPrice, product.fallbackPriceCurrency), product.fallbackHost && `listed from ${product.fallbackHost}`].filter(Boolean).join(' · ');
  const content = (
    <>
      <div className='catalog-row-photo'>
        <SafeImage className='ltc-photo-image' source={product.image} alt='' placeholderText='' />
      </div>
      <div className='catalog-row-text'>
        <div className='catalog-row-title'>{product.title}</div>
        <div className='catalog-row-meta'>{meta}</div>
      </div>
      {keyLabel && <span className='ltc-key'>{keyLabel}</span>}
    </>
  );
  if (!onPick) {
    return (
      <div key={product.id} className='catalog-row'>
        {content}
      </div>
    );
  }
  return (
    <button key={product.id} type='button' className='catalog-row choice' onClick={onPick}>
      {content}
    </button>
  );
};

const renderProductTile = ({ product, keyLabel, onPick }) => (
  <button key={product.id} type='button' className='catalog-tile choice' onClick={onPick}>
    <div className='catalog-tile-photo'>
      <SafeImage className='ltc-photo-image' source={product.image} alt='' placeholderText='No photo' />
      <span className='ltc-key'>{keyLabel}</span>
    </div>
    <div className='catalog-tile-title'>{product.title}</div>
    <div className='catalog-tile-meta'>{[formatPrice(product.fallbackPrice, product.fallbackPriceCurrency), `#${product.id}`].filter(Boolean).join(' · ')}</div>
  </button>
);
